import React from 'react';
import { useAuthStore } from '../store';
import { GlassCard, PageHeader } from '../components/GlassUI';
import { FaCalendarAlt, FaCheckCircle, FaExclamationTriangle, FaUsers, FaUserTag, FaCreditCard } from 'react-icons/fa';
import { PricingPage } from './PricingPage';

const planLabels: Record<string, string> = {
  FREE: 'Free Trial',
  BASIC: 'Basic',
  PRO: 'Pro',
  ENTERPRISE: 'Enterprise',
};

const planColors: Record<string, string> = {
  FREE: 'bg-slate-100 text-slate-800',
  BASIC: 'bg-blue-100 text-blue-800',
  PRO: 'bg-purple-100 text-purple-800',
  ENTERPRISE: 'bg-amber-100 text-amber-800',
};

const statusColors: Record<string, string> = {
  TRIAL: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  EXPIRED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-slate-100 text-slate-800',
  PAST_DUE: 'bg-yellow-100 text-yellow-800',
};

export const SubscriptionPage: React.FC = () => {
  const { subscription } = useAuthStore();

  if (!subscription) {
    return <PricingPage />;
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (dateStr?: string) => {
    if (!dateStr) return 0;
    const end = new Date(dateStr);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const daysLeft = getDaysRemaining(subscription.trialEnd || subscription.endDate);
  const isExpiring = daysLeft > 0 && daysLeft <= 3;

  return (
    <div className='max-w-4xl mx-auto'>
        <PageHeader
          title='My Subscription'
          subtitle='Manage your CRM access plan and billing.'
          icon={<FaCreditCard />}
        />

        {/* Current Plan Card */}
        <GlassCard className='p-6 mb-8'>
          <div className='flex items-start justify-between flex-wrap gap-4'>
            <div>
              <div className='flex items-center gap-3 mb-2'>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${planColors[subscription.plan] || 'bg-slate-100'}`}>
                  {planLabels[subscription.plan] || subscription.plan}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusColors[subscription.status] || 'bg-slate-100'}`}>
                  {subscription.status}
                </span>
              </div>
              <h2 className='text-2xl font-bold text-slate-900 mt-2'>
                {subscription.planName || planLabels[subscription.plan] || subscription.plan} Plan
              </h2>
            </div>

            {(subscription.status === 'TRIAL' || subscription.status === 'ACTIVE') && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                isExpiring ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
              }`}>
                {isExpiring ? <FaExclamationTriangle /> : <FaCheckCircle />}
                <span className='font-semibold'>
                  {daysLeft} day{daysLeft > 1 ? 's' : ''} remaining
                </span>
              </div>
            )}
          </div>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-6'>
            <div className='flex items-center gap-3 bg-slate-50 rounded-xl p-4'>
              <FaCalendarAlt className='text-slate-400 text-xl' />
              <div>
                <p className='text-xs text-slate-500'>
                  {subscription.status === 'TRIAL' ? 'Trial Ends' : 'Expires'}
                </p>
                <p className='font-semibold text-slate-900'>
                  {formatDate(subscription.trialEnd || subscription.endDate)}
                </p>
              </div>
            </div>
            <div className='flex items-center gap-3 bg-slate-50 rounded-xl p-4'>
              <FaUsers className='text-slate-400 text-xl' />
              <div>
                <p className='text-xs text-slate-500'>Max Customers</p>
                <p className='font-semibold text-slate-900'>{subscription.maxCustomers?.toLocaleString() || 'N/A'}</p>
              </div>
            </div>
            <div className='flex items-center gap-3 bg-slate-50 rounded-xl p-4'>
              <FaUserTag className='text-slate-400 text-xl' />
              <div>
                <p className='text-xs text-slate-500'>Max Staff</p>
                <p className='font-semibold text-slate-900'>{subscription.maxStaff || 'N/A'}</p>
              </div>
            </div>
          </div>
        </GlassCard>

        {/* Upgrade / Plans */}
        <div className='mb-6'>
          <h2 className='text-2xl font-bold text-slate-900 mb-2'>Upgrade or Change Plan</h2>
          <p className='text-slate-600 mb-6'>Choose a plan that fits your business needs.</p>
          <PricingPage standalone={false} />
        </div>
    </div>
  );
};

export default SubscriptionPage;
